import React, { useEffect, useMemo, useState } from "react";
import { NavLink, RouteComponentProps, withRouter } from "react-router-dom";
import { Nav } from "reactstrap";
import { IRoutes, IRoute } from "../../../interfaces/route";
import { ISubmenus } from "../../../interfaces/submenus";
import { connect } from "react-redux";
import { IApplicationState } from "../../../redux/reducers";
import { Icon } from "@iconify/react";
import crownIcon from "@iconify-icons/mdi/crown";
import menuIcon from "@iconify-icons/mdi/menu";
import closeIcon from "@iconify-icons/mdi/close";
import { INotification } from "../../../interfaces/notification";
import notificationService from "../../../services/notificationService";
import userService from "../../../services/userService";
import { LogOut, ChevronUp } from "react-feather";
import JF_LOGO_NEW from "../../../assets/images/JF Color Logomark.png";
import SubmenuToggle from "../SubmenuToggle";
import { routes } from "../../../routes/routes";
import { hasSupplierManufacturerAccess } from "../../../utils/subscription";
import LogoutConfirmationModal from "../../modals/LogoutConfirmationModal";

interface ISidebarProps extends RouteComponentProps {
  routes: IRoutes;
  role: string;
  profile: any;
  notifications: INotification[];
  getAllNotifications: () => Promise<any>;
  logout: () => void;
}

interface ISubmenuState {
  name: string;
  routes: ISubmenus;
}

const Sidebar: React.FC<ISidebarProps> = (props) => {
  const INIT_SUBMENU: ISubmenuState = { name: "", routes: [] };
  const [loadingNotifications, setLoadingNotifications] = useState<boolean>();
  const [submenus, setSubmenus] = useState<ISubmenuState>(INIT_SUBMENU);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isTablet, setIsTablet] = useState<boolean>(false);
  const [isSubmenuOpen, setIsSubmenuOpen] = useState<boolean>(false);
  const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false);

  // Calculate profile completion percentage
  const calculateProfileCompletion = useMemo(() => {
    if (!props.profile) return 0;

    const fields = [
      { value: props.profile.first_name, weight: 1 },
      { value: props.profile.last_name, weight: 1 },
      { value: props.profile.email, weight: 1 },
      { value: props.profile.phone_number, weight: 1 },
      { value: props.profile.city, weight: 1 },
      { value: props.profile.state, weight: 1 },
      { value: props.profile.country, weight: 1 },
      { value: props.profile.user_photo, weight: 1 },
      { value: props.profile.company?.name, weight: 1 },
      { value: props.profile.company?.company_goal, weight: 1.5 },
      { value: props.profile.company?.company_objective, weight: 1.5 },
    ];

    const totalWeight = fields.reduce((sum, field) => sum + field.weight, 0);
    const completedWeight = fields.reduce((sum, field) => {
      const hasValue = field.value &&
        (typeof field.value === 'string' ? field.value.trim().length > 0 : true);
      return sum + (hasValue ? field.weight : 0);
    }, 0);

    return Math.round((completedWeight / totalWeight) * 100);
  }, [props.profile]);

  // Enhanced responsive detection with zoom support
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;

      // Standard responsive breakpoints:
      // - Mobile: < 768px (hide sidebar, show mobile menu)
      // - Tablet/iPad: 768-1023px (show sidebar with toggle submenu)
      // - Desktop: >= 1024px (show sidebar with auto-open submenu)
      const mobile = width < 768;
      const tablet = width >= 768 && width < 1024; // iPad range (exclude 1024px)

      setIsMobile(mobile);
      setIsTablet(tablet);

      // For iPad, ensure sidebar is visible by default
      if (tablet && !mobile) {
        // Set sidebar type to 'full' for iPad to show sidebar
        const mainWrapper = document.getElementById('main-wrapper');
        if (mainWrapper) {
          mainWrapper.setAttribute('data-sidebartype', 'full');
        }
      }
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  // Enhanced route finding and submenu logic
  useEffect(() => {
    // More robust route matching - check for exact match first, then partial match
    const found = props.routes.find((r) => {
      const currentPath = props.location.pathname;
      // Exact match first
      if (currentPath === r.path) return true;
      // Then check if current path starts with route path (for sub-routes)
      if (currentPath.startsWith(r.path + '/')) return true;
      // Fallback to indexOf for backward compatibility
      return currentPath.indexOf(r.path) > -1;
    });


    if (props.notifications === undefined) {
      setLoadingNotifications(true);
      props.getAllNotifications().finally(() => setLoadingNotifications(false));
    }

    if (found && found.submenus && found.submenus.length > 0) {
      setSubmenus({ name: found.path, routes: found.submenus });
    } else {
      setSubmenus(INIT_SUBMENU);
      // Close submenu when switching to a tab without submenu
      resetSubmenuState();
    }

    // Close mobile menu on route change
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  }, [props.location, isMobile, props.routes]);

  // Initialize submenu on component mount
  useEffect(() => {
    const currentPath = props.location.pathname;
    const isAccountPage = currentPath.includes('/app/account');
    const isIngredientsPage = currentPath.includes('/app/ingredients');

    // Handle Account submenu
    if (isAccountPage) {
      const accountRoute = props.routes.find(r => r.path === '/app/account');
      if (accountRoute && accountRoute.submenus) {
        setSubmenus({ name: accountRoute.path, routes: accountRoute.submenus });

        // Set submenu state based on device type
        if (isMobile || isTablet) {
          // On mobile/tablet, submenu should be closed by default
          setIsSubmenuOpen(false);
          const mainWrapper = document.getElementById('main-wrapper');
          if (mainWrapper) {
            mainWrapper.classList.remove('submenu-open');
          }
        } else {
          // On desktop, submenu should be open by default
          setIsSubmenuOpen(true);
          const mainWrapper = document.getElementById('main-wrapper');
          if (mainWrapper) {
            mainWrapper.classList.add('submenu-open');
          }
        }
      }
    }
    // Ingredients submenu is now inline, no need for separate panel
    else {
      // Reset submenu if not on account or ingredients page
      setSubmenus(INIT_SUBMENU);
      setIsSubmenuOpen(false);

      // Remove class from main wrapper
      const mainWrapper = document.getElementById('main-wrapper');
      if (mainWrapper) {
        mainWrapper.classList.remove('account-submenu-visible');
        mainWrapper.classList.remove('submenu-open');
      }
    }
  }, [props.location.pathname, props.routes, isMobile, isTablet]); // Run when pathname or routes change

  // Additional effect to ensure main wrapper class is applied correctly
  useEffect(() => {
    const mainWrapper = document.getElementById('main-wrapper');
    if (mainWrapper) {
      // FIXED: Only add account-submenu-visible class on tablet and desktop, NOT mobile
      // This prevents iPad-specific CSS from affecting mobile layout
      if (props.location.pathname.includes('/app/account') && !isMobile) {
        mainWrapper.classList.add('account-submenu-visible');
      } else {
        mainWrapper.classList.remove('account-submenu-visible');
      }
    }
  }, [props.location.pathname, isMobile, isTablet]);

  // Keyboard support for closing submenu
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isSubmenuOpen) {
        setIsSubmenuOpen(false);
        const mainWrapper = document.getElementById('main-wrapper');
        if (mainWrapper) {
          mainWrapper.classList.remove('submenu-open');
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSubmenuOpen]);

  // Cleanup effect to reset submenu state when component unmounts
  useEffect(() => {
    return () => {
      // Reset submenu state when component unmounts
      resetSubmenuState();

      // Remove account submenu class from main wrapper
      const mainWrapper = document.getElementById('main-wrapper');
      if (mainWrapper) {
        mainWrapper.classList.remove('account-submenu-visible');
      }
    };
  }, []);

  // Global effect to handle browser navigation (back/forward buttons)
  useEffect(() => {
    const handlePopState = () => {
      // Reset submenu state when user navigates using browser buttons
      resetSubmenuState();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Enhanced logo expansion with mobile consideration
  const expandLogo = () => {
    if (!isMobile) {
      document.getElementById("logobg")?.classList.toggle("expand-logo");
    }
  };

  // Mobile menu toggle
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Prevent body scroll when mobile menu is open
    document.body.style.overflow = !isMobileMenuOpen ? 'hidden' : 'auto';
  };


  // Close mobile menu when clicking outside
  const handleBackdropClick = () => {
    if (isMobile && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
      document.body.style.overflow = 'auto';
    }
  };

  // Close submenu when clicking outside
  const handleSubmenuBackdropClick = (e?: React.MouseEvent | React.TouchEvent) => {
    // Prevent default to avoid any conflicts
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    setIsSubmenuOpen(false);
    const mainWrapper = document.getElementById('main-wrapper');
    if (mainWrapper) {
      mainWrapper.classList.remove('submenu-open');
    }
  };

  // Utility function to reset submenu state
  const resetSubmenuState = () => {
    setIsSubmenuOpen(false);
    const mainWrapper = document.getElementById('main-wrapper');
    if (mainWrapper) {
      mainWrapper.classList.remove('submenu-open');
    }
  };

  // Utility function to update submenu state without affecting header
  const updateSubmenuState = (isOpen: boolean) => {
    setIsSubmenuOpen(isOpen);
    const mainWrapper = document.getElementById('main-wrapper');
    if (mainWrapper) {
      if (isOpen) {
        mainWrapper.classList.add('submenu-open');
      } else {
        mainWrapper.classList.remove('submenu-open');
      }
    }
  };

  // Enhanced notification counting
  const getRouteNotificationCount = (name: string) => {
    const routeNotifications = (type: string) =>
      (props.notifications || []).filter(
        (n: INotification) => n.message_type.toLowerCase() === type.toLowerCase() && !n.is_read
      );

    switch (name) {
      case "Products": return routeNotifications("Product").length;
      case "Ingredients": return routeNotifications("Ingredient").length;
      case "Packaging": return routeNotifications("Packaging").length;
      case "Personas": return routeNotifications("Personas").length;
      case "Analytics": return routeNotifications("Analytics").length;
      case "Integrations": return routeNotifications("Integrations").length;
      case "Account": return routeNotifications("Account").length;
      case "Admin": return routeNotifications("Admin").length;
      case "Notifications": return (props.notifications || []).filter((n) => !n.is_read).length;
      default: return 0;
    }
  };

  const getJoyRideId = (name: string) => {
    switch (name) {
      case "Company Settings": return "company-settings-joyride";
      case "Dashboard": return "dashboard-joyride";
      case "Integrations": return "integrations-joyride";
      case "Ingredients": return "ingredients-joyride";
      case "Packaging": return "packaging-joyride";
      default: return "";
    }
  };

  // Section groups
  const MENU = useMemo(
    () => ["Overview", "Generate", "Ingredients", "Products", "Suppliers", "Packaging"],
    []
  );
  const SUPPORT = useMemo(
    () => ["Guava", "Analytics", "Integrations", "Documents"],
    []
  );

  const topRoutes = useMemo(
    () => props.routes.filter((r) => !r.atBottom && !r.inactive),
    [props.routes]
  );
  const bottomRoutes = useMemo(
    () => {
      const routes = props.routes.filter((r) => r.atBottom && !r.inactive);
      // DEBUG: Log bottom routes on mobile
      // if (isMobile) {
      //   console.log('Bottom routes:', routes.map(r => ({ name: r.name, path: r.path, atBottom: r.atBottom })));
      // }
      return routes;
    },
    [props.routes, isMobile]
  );

  // Enhanced route rendering with better mobile support
  const renderRoute = (route: IRoute) => {
    const companyHasAccess =
      route.permission_name &&
        props.profile != undefined &&
        props.profile.role !== "JF_ADMIN" &&
        props.profile.role !== "JF_STAFF"
        ? props.profile?.company[route.permission_name] !== "disable" &&
        !route.locked_for?.includes(props.profile?.company.subscription_type)
        : true;

    if (!companyHasAccess && !route.upgrade_component) return null;
    if (route.roles && !route.roles.includes(props.role)) return null;
    if (route.redirect || route.name === "Upgrade") return null;

    // DEBUG: Log when rendering bottom routes on mobile
    // if (route.atBottom && isMobile) {
    //   console.log('Rendering bottom route:', route.name, {
    //     path: route.path,
    //     icon: route.icon,
    //     atBottom: route.atBottom,
    //     isMobile: isMobile
    //   });
    // }

    const notifCount = getRouteNotificationCount(route.name);

    // Check if user has subscription access (for Generate and other paid features)
    const hasSubscriptionAccess = hasSupplierManufacturerAccess(props.profile);

    // Show crown icon if:
    // 1. User is not an admin
    // 2. Route requires permission (has permission_name) OR is Generate (paid service)
    // 3. User doesn't have access
    const shouldShowCrown =
      props.profile &&
      props.profile.role !== "JF_ADMIN" &&
      props.profile.role !== "JF_STAFF" &&
      (
        (route.permission_name && !companyHasAccess) || // Route with permission check
        (route.name === "Generate" && !hasSubscriptionAccess) // Generate is a paid service
      );

    // Check if this is Ingredients route with submenus and we're on ingredients page
    const isIngredientsWithSubmenu = route.name === "Ingredients" &&
      route.submenus &&
      route.submenus.length > 0 &&
      props.location.pathname.includes('/app/ingredients');
    const showInlineSubmenu = isIngredientsWithSubmenu;

    return (
      <li
        className={(submenus.name === route.path ? "active " : "") + "sidebar-item" + (showInlineSubmenu ? " has-submenu" : "")}
        key={route.path}
      >
        <NavLink
          to={route.path}
          id={getJoyRideId(route.name)}
          className={`sidebar-link ${route.name.toLowerCase().replace(/\s+/g, "-")}`}
          activeClassName="active"
          onClick={() => {
            if (isMobile) {
              setIsMobileMenuOpen(false);
              document.body.style.overflow = 'auto';
            }
          }}
        >
          {typeof route.icon === "string" ? <i className={route.icon} /> : route.icon}
          <span className="hide-menu">{route.name}</span>
          {/* Show crown icon only when user doesn't have subscription access */}
          {shouldShowCrown && (
            <Icon color="#f4a850" className="icon-crown" icon={crownIcon} width={16} height={16} />
          )}

          {route.name === "Ingredients" ? (
            <>
              <span className="sidebar-badge">
                {notifCount > 99 ? "99+" : `${Math.max(1, notifCount || 10)}+`}
              </span>
              {route.submenus && route.submenus.length > 0 && (
                <ChevronUp size={16} className="submenu-chevron" />
              )}
            </>
          ) : null}
          {/* Show chevron for other routes with submenus, but not for Account */}
          {route.name !== "Ingredients" && route.name !== "Account" && route.submenus && route.submenus.length > 0 && (
            <ChevronUp size={16} className="submenu-chevron" />
          )}
        </NavLink>

        {/* Inline submenu for Ingredients */}
        {showInlineSubmenu && (
          <ul className="inline-submenu">
            {route.submenus.map((submenu, index) => (
              <li key={submenu.name + index} className="inline-submenu-item">
                <NavLink
                  to={submenu.route}
                  className="inline-submenu-link"
                  activeClassName="active"
                  onClick={() => {
                    if (isMobile) {
                      setIsMobileMenuOpen(false);
                      document.body.style.overflow = 'auto';
                    }
                  }}
                >
                  {submenu.name}
                </NavLink>
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  };

  return (
    <>
      {/* Mobile Menu Button */}
      {isMobile && (
        <button
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
          aria-expanded={isMobileMenuOpen}
        >
          <Icon icon={isMobileMenuOpen ? closeIcon : menuIcon} width="24" height="24" />
        </button>
      )}

      {/* Mobile Backdrop */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="mobile-backdrop"
          onClick={handleBackdropClick}
          aria-hidden="true"
        />
      )}

      <div style={{ display: "flex" }}>
        <aside
          className={`left-sidebar jf-sidebar pt-3 ${isMobile ? 'mobile-sidebar' : ''} ${isTablet ? 'tablet-sidebar' : ''} ${isMobileMenuOpen ? 'mobile-open' : ''}`}
          id="sidebarbg"
          data-sidebarbg="skin6"
          onMouseEnter={expandLogo}
          onMouseLeave={expandLogo}
        >
          {/* Enhanced Brand Section */}
          <div className="sidebar-brand">
            <a className="d-flex align-items-center">
              <b className="logo-icon">
                <img
                  src={JF_LOGO_NEW}
                  alt="Journey Foods Logo Mark"
                  className="dark-logo d-block"
                  width={isMobile ? 40 : 50}
                />
              </b>
              <span className="brand-text ml-2">JourneyFoods</span>
            </a>
            {/* Mobile Close Button */}
            {isMobile && (
              <button
                className="mobile-close-btn"
                onClick={toggleMobileMenu}
                aria-label="Close mobile menu"
              >
                <Icon icon={closeIcon} width="20" height="20" />
              </button>
            )}
          </div>

          <div className={`scroll-sidebar ${isMobile ? 'mt-3' : 'mt-4'} pt-2`}>
            <div className="sidebar-nav">
              <div className="d-flex sidebar-content" style={{ minHeight: "100%", flexDirection: "column" }}>
                {/* MENU */}
                <div className="nav-small-cap">MENU</div>
                <Nav className="sidebarnav">
                  {topRoutes.filter((r) => MENU.includes(r.name)).map(renderRoute)}
                </Nav>

                <div className="nav-divider" />

                {/* SUPPORT */}
                <div className="nav-small-cap">SUPPORT</div>
                <Nav className="sidebarnav">
                  {topRoutes.filter((r) => SUPPORT.includes(r.name)).map(renderRoute)}
                </Nav>

                {/* Enhanced Profile card with responsive design - positioned above bottom nav */}
                <div className="sidebar-footer" style={{ marginTop: isMobile ? "20px" : "auto" }}>
                  <div className="profile-card">
                    <div className="profile-header">
                      {(isMobile || isTablet) && (
                        <div className="profile-avatar">
                          <div className="avatar-circle">
                            {props.profile?.user_photo ? (
                              <img
                                src={props.profile.user_photo}
                                alt="Profile"
                                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                              />
                            ) : (
                              <span>👤</span>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="profile-info">
                        <div className="title">Profile Completion</div>
                        <div className="progress-wrap">
                          <div
                            className="progress-bar"
                            style={{
                              width: `${calculateProfileCompletion}%`,
                              background: calculateProfileCompletion >= 80
                                ? 'linear-gradient(90deg, #21e80f 0%, #1bc71a 100%)'
                                : calculateProfileCompletion >= 50
                                  ? 'linear-gradient(90deg, #f4a850 0%, #f59e0b 100%)'
                                  : 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)'
                            }}
                          />
                        </div>
                        <div className="meta">{calculateProfileCompletion}% profile completeness</div>
                      </div>
                    </div>
                    <button
                      className="cta"
                      type="button"
                      onClick={() => {
                        if (isMobile) {
                          setIsMobileMenuOpen(false);
                          document.body.style.overflow = 'auto';
                        }
                        props.history.push(routes.PROFILE);
                      }}
                    >
                      Set Contact Info
                    </button>
                  </div>
                </div>

                {/* Bottom routes */}
                <div className="bottom-nav">
                  <Nav className="sidebarnav">
                    {bottomRoutes.map(renderRoute)}

                    {/* Logout Option */}
                    <li className="sidebar-item">
                      <button
                        className="sidebar-link logout-link"
                        onClick={() => {
                          if (isMobile) {
                            setIsMobileMenuOpen(false);
                            document.body.style.overflow = 'auto';
                          }
                          setShowLogoutModal(true);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          width: '100%',
                          textAlign: 'left',
                          padding: '10px 15px',
                          color: '#8a8a8a',
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          transition: 'color 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = '#fff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = '#8a8a8a';
                        }}
                      >
                        <LogOut size={18} className="me-2" />
                        <span className="hide-menu">Logout</span>
                      </button>
                    </li>
                  </Nav>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Enhanced Submenu with responsive behavior - Only for Account */}
        {(submenus.name !== "" && submenus.name === '/app/account') && (
          <>
            {/* Mobile/Tablet: Show backdrop and toggle button */}
            {(isMobile || isTablet) && (
              <>
                {/* Submenu Backdrop */}
                <div
                  className={`submenu-backdrop ${isSubmenuOpen ? 'active' : ''}`}
                  onClick={handleSubmenuBackdropClick}
                  onTouchEnd={handleSubmenuBackdropClick}
                  aria-hidden="true"
                />

                {/* Submenu Toggle Button - Only for mobile/tablet */}
                <SubmenuToggle
                  isOpen={isSubmenuOpen}
                  onToggle={setIsSubmenuOpen}
                />
              </>
            )}

            {/* Submenu Panel */}
            <div
              className={`account-submenu ${isMobile || isTablet ? (isSubmenuOpen ? 'mobile-open' : 'mobile-closed') : 'desktop-visible'}`}
            >
              {/* Submenu Items */}
              {(submenus.routes.length > 0 ? submenus.routes : [
                { name: "Profile Settings", route: "/app/account/profile" },
                { name: "Company Settings", route: "/app/account/company" },
                { name: "Preferences", route: "/app/account/preferences" },
                { name: "Billing", route: "/app/account/billing" }
              ]).map((s, index) => (
                <NavLink
                  key={s.name + index}
                  to={s.route}
                  className="submenu-link"
                  activeClassName="active"
                  onClick={() => {
                    // Close submenu on mobile/tablet when user clicks on any menu item
                    if (isMobile || isTablet) {
                      setIsSubmenuOpen(false);
                      const mainWrapper = document.getElementById('main-wrapper');
                      if (mainWrapper) {
                        mainWrapper.classList.remove('submenu-open');
                      }
                    }
                  }}
                >
                  <div
                    id={getJoyRideId(s.name)}
                    className="submenu-item"
                  >
                    {s.name}
                  </div>
                </NavLink>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Logout Confirmation Modal */}
      <LogoutConfirmationModal
        isOpen={showLogoutModal}
        toggle={() => setShowLogoutModal(false)}
        onConfirm={() => {
          props.logout();
        }}
      />
    </>
  );
};

const mapStateToProps = (state: IApplicationState) => ({
  role: state.user.role,
  profile: state.user.profile,
  notifications: state.notification.all || []
});

const mapDispatchToProps = {
  getAllNotifications: () => notificationService.getAllNotifications(),
  logout: () => userService.logout()
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Sidebar));